import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { prefersReducedMotion } from './EnvelopeReveal';

/** Delay before the one-off "here's the other side" auto-flip. */
const AUTO_FLIP_DELAY_MS = 2600;

export interface InvitationPagesProps {
  /** Artwork URLs in flip order (cover first). */
  pages: string[];
  /** Used for the page alt text. */
  eventName: string;
  accentColor: string;
  /** Auto-flip to page 2 once on first view. Default true. */
  autoFlip?: boolean;
  autoFlipDelayMs?: number;
}

/**
 * Flip-through view of a multi-page invitation. Pages are stacked and the
 * current one swings away on its left edge, like turning a card over.
 *
 * A single-page invitation renders as a plain image with no controls, so the
 * same component serves both cases. On first view it turns to page two by
 * itself — guests otherwise never discover there is a second side — and then
 * leaves navigation to the guest (any interaction cancels the pending flip).
 */
export function InvitationPages({
  pages,
  eventName,
  accentColor,
  autoFlip = true,
  autoFlipDelayMs = AUTO_FLIP_DELAY_MS,
}: InvitationPagesProps) {
  const [index, setIndex] = useState(0);
  const interactedRef = useRef(false);
  const multiPage = pages.length > 1;

  useEffect(() => {
    if (!multiPage || !autoFlip || prefersReducedMotion()) return;
    const timer = setTimeout(() => {
      if (!interactedRef.current) setIndex((current) => (current === 0 ? 1 : current));
    }, autoFlipDelayMs);
    return () => clearTimeout(timer);
  }, [multiPage, autoFlip, autoFlipDelayMs]);

  const goTo = (next: number) => {
    interactedRef.current = true;
    setIndex(Math.min(Math.max(next, 0), pages.length - 1));
  };

  if (pages.length === 0) return null;

  if (!multiPage) {
    return (
      <img
        src={pages[0]}
        alt={`Invitation to ${eventName}`}
        className="w-full rounded-2xl shadow-sm"
      />
    );
  }

  return (
    <div>
      <div className="invite-pages relative">
        {/* Sizing sheet: the stacked pages are absolutely positioned, so the
            first page (in flow, but invisible) gives the deck its height. */}
        <img src={pages[0]} alt="" aria-hidden="true" className="w-full rounded-2xl invisible" />
        {pages.map((page, pageIndex) => (
          <button
            key={page}
            type="button"
            // Advancing wraps so a guest can keep tapping through the deck.
            onClick={() => goTo(pageIndex === pages.length - 1 ? 0 : pageIndex + 1)}
            aria-label={`Invitation page ${pageIndex + 1} of ${pages.length} — tap to turn`}
            aria-hidden={pageIndex < index}
            tabIndex={pageIndex === index ? 0 : -1}
            className="invite-page block w-full p-0 border-0 bg-transparent cursor-pointer"
            data-stacked="true"
            data-turned={pageIndex < index ? 'true' : 'false'}
            style={{ zIndex: pages.length - pageIndex }}
          >
            <img
              src={page}
              alt={`Invitation to ${eventName}, page ${pageIndex + 1}`}
              className="w-full rounded-2xl shadow-sm"
            />
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          disabled={index === 0}
          aria-label="Previous page"
          className="p-1.5 rounded-full border border-gray-200 bg-white/80 disabled:opacity-40"
          style={{ color: accentColor }}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-1.5">
          {pages.map((page, pageIndex) => (
            <button
              key={page}
              type="button"
              onClick={() => goTo(pageIndex)}
              aria-label={`Go to page ${pageIndex + 1}`}
              aria-current={pageIndex === index}
              className="w-2 h-2 rounded-full transition-opacity"
              style={{
                backgroundColor: accentColor,
                opacity: pageIndex === index ? 1 : 0.3,
              }}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          disabled={index === pages.length - 1}
          aria-label="Next page"
          className="p-1.5 rounded-full border border-gray-200 bg-white/80 disabled:opacity-40"
          style={{ color: accentColor }}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
